import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import IntlMessages from 'util/IntlMessages';
import { useLocalizedMessage } from 'util/useLocalizedMessage';

const COPY = {
  missing_session: {
    eyebrow: 'ui.shellRecoverySurface.connectionInterrupted',
    title: "ui.shellRecoverySurface.reconnectToTheAdmin",
    message: 'ui.shellRecoverySurface.missingSession.message',
  },
  session_expired: {
    eyebrow: 'ui.shellRecoverySurface.sessionExpired',
    title: "ui.shellRecoverySurface.reconnectToContinue",
    message: 'ui.shellRecoverySurface.sessionExpired.message',
  },
  auth_recovery: {
    eyebrow: 'ui.shellRecoverySurface.connectionProblem',
    title: "ui.shellRecoverySurface.weCouldnTConnectToTheSearchService",
    message: 'ui.shellRecoverySurface.authRecovery.message',
  },
};

const ERROR_EXPLANATIONS = {
  GATEWAY_SESSION_UNAVAILABLE: 'ui.shellRecoverySurface.error.gatewayUnavailable',
  CREDENTIALS_MISSING: 'ui.shellRecoverySurface.error.credentialsMissing',
  INSTALLATION_NOT_FOUND: 'ui.shellRecoverySurface.error.installationMissing',
  AUTH_RECOVERY: 'ui.shellRecoverySurface.error.authRecovery',
};

const normalizeErrorCode = (errorCode, variant) => {
  const fallback = variant === 'auth_recovery' ? 'AUTH_RECOVERY' : variant.toUpperCase();
  if (!errorCode || !/^[A-Z0-9_-]{1,64}$/i.test(errorCode)) return fallback;
  return errorCode.toUpperCase();
};

const buildDiagnosticDetails = (variant, errorCode) => {
  const gatewayUrl = typeof window !== 'undefined'
    ? window.KEA_PRO_GATEWAY_URL || 'not configured'
    : 'not available';
  const appUrl = typeof window !== 'undefined' ? window.location.origin : 'not available';

  return [
    'Kea Labs admin recovery details',
    `time: ${new Date().toISOString()}`,
    `recovery_type: ${variant}`,
    `error_code: ${errorCode}`,
    `admin_app: ${appUrl}`,
    `gateway: ${gatewayUrl}`,
  ].join('\n');
};

const copyText = async (text) => {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch (_error) {
      // Embedded cross-origin Back Office frames may expose the Clipboard API
      // while denying clipboard-write. Continue with the user-gesture fallback.
    }
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  try {
    textarea.focus();
    textarea.select();
    textarea.setSelectionRange(0, textarea.value.length);
    const copied = document.execCommand?.('copy');
    if (!copied) throw new Error('Clipboard copy failed');
  } finally {
    document.body.removeChild(textarea);
  }
};

export function ShellRecoverySurface({ variant, errorCode }) {
  const t = useLocalizedMessage();
  const copy = COPY[variant] || COPY.missing_session;
  const safeVariant = COPY[variant] ? variant : 'missing_session';
  const safeErrorCode = normalizeErrorCode(errorCode, safeVariant);
  const explanation = ERROR_EXPLANATIONS[safeErrorCode] || ERROR_EXPLANATIONS.AUTH_RECOVERY;
  const diagnostics = useMemo(
    () => buildDiagnosticDetails(safeVariant, safeErrorCode),
    [safeVariant, safeErrorCode],
  );
  const [copyStatus, setCopyStatus] = useState('idle');

  const handleCopy = async () => {
    try {
      await copyText(diagnostics);
      setCopyStatus('copied');
    } catch (_error) {
      setCopyStatus('failed');
    }
  };

  return (
    <main style={styles.root} data-testid={`shell-recovery-${safeVariant}`}>
      <section style={styles.card} aria-labelledby="shell-recovery-title">
        <p style={styles.eyebrow}>{t(copy.eyebrow)}</p>
        <h1 id="shell-recovery-title" style={styles.title}>{t(copy.title)}</h1>
        <p style={styles.message}>{t(copy.message)}</p>

        <div style={styles.status} data-testid="shell-recovery-status">
          <p style={styles.statusText}>{t(explanation)}</p>
          <code style={styles.errorCode}>{safeErrorCode}</code>
        </div>

        <div style={styles.actions}>
          <a href="/embedded/app" style={styles.primaryAction}>
            <IntlMessages id="ui.shellRecoverySurface.tryAgain" />
          </a>
          <button
            type="button"
            style={styles.secondaryAction}
            onClick={handleCopy}
            data-testid="copy-recovery-details"
          >
            <IntlMessages id="ui.shellRecoverySurface.copyDiagnosticDetails" />
          </button>
        </div>

        <p style={styles.supportHint}>
          <IntlMessages id="ui.shellRecoverySurface.ifTheProblemContinuesCopyTheDiagnosticDetailsAndSend" />
        </p>
        <p style={styles.copyStatus} aria-live="polite">
          {copyStatus === 'copied' && t('ui.shellRecoverySurface.copy.success')}
          {copyStatus === 'failed' && t('ui.shellRecoverySurface.copy.failed')}
        </p>
      </section>
    </main>
  );
}

ShellRecoverySurface.propTypes = {
  variant: PropTypes.oneOf(['missing_session', 'session_expired', 'auth_recovery']).isRequired,
  errorCode: PropTypes.string,
};

ShellRecoverySurface.defaultProps = {
  errorCode: null,
};

const styles = {
  root: {
    position: 'fixed',
    inset: 0,
    width: '100vw',
    minHeight: '100vh',
    padding: 24,
    boxSizing: 'border-box',
    background: '#f5f8f7',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
  },
  card: {
    width: '100%',
    maxWidth: 520,
    padding: 32,
    boxSizing: 'border-box',
    border: '1px solid #ddeae5',
    borderRadius: 12,
    background: '#fff',
    boxShadow: '0 12px 32px rgba(30, 34, 72, 0.08)',
  },
  eyebrow: {
    margin: '0 0 8px',
    color: 'var(--kea-color-primary)',
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  },
  title: {
    margin: 0,
    color: '#1e2248',
    fontSize: 24,
    fontWeight: 700,
    letterSpacing: '-0.02em',
    lineHeight: 1.25,
  },
  message: {
    margin: '12px 0 0',
    maxWidth: '60ch',
    color: '#66708a',
    fontSize: 14,
    lineHeight: 1.6,
  },
  status: {
    marginTop: 24,
    padding: '12px 16px',
    border: '1px solid #e6eeeb',
    borderRadius: 8,
    background: '#f8fbfa',
  },
  statusText: {
    margin: 0,
    color: '#1e2248',
    fontSize: 13,
    lineHeight: 1.5,
  },
  errorCode: {
    display: 'inline-block',
    marginTop: 6,
    color: '#66708a',
    fontSize: 11,
  },
  actions: {
    marginTop: 24,
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
  },
  primaryAction: {
    minHeight: 42,
    padding: '0 20px',
    borderRadius: 999,
    background: 'var(--kea-color-primary)',
    color: '#fff',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 13,
    fontWeight: 600,
    textDecoration: 'none',
  },
  secondaryAction: {
    minHeight: 42,
    padding: '0 20px',
    border: '1px solid #cfded8',
    borderRadius: 999,
    background: '#fff',
    color: '#1e2248',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 13,
    fontWeight: 600,
  },
  supportHint: {
    margin: '16px 0 0',
    color: '#66708a',
    fontSize: 12,
    lineHeight: 1.5,
  },
  copyStatus: {
    minHeight: 18,
    margin: '6px 0 0',
    color: 'var(--kea-color-primary)',
    fontSize: 12,
    lineHeight: 1.5,
  },
};
